import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../api';

const STATUS_OPTIONS = ['pending', 'processing', 'completed', 'failed', 'cancelled'];

const STATUS_COLORS = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'default'
};

export default function MintRequestsManager() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    userId: '',
    assetId: '',
    contract: '',
    tokenId: ''
  });
  
  const queryClient = useQueryClient();

  // Debug: Monitor filter changes
  useEffect(() => {
    console.log('Filters changed:', filters);
  }, [filters]);

  // Create a stable query key
  const queryKey = useMemo(() => {
    return ['mintRequests', page + 1, rowsPerPage, JSON.stringify(filters)];
  }, [page, rowsPerPage, filters]);

  // Fetch mint requests
  const { data: mintRequestsData, isLoading, isError, error } = useQuery({
    queryKey: ['mintRequests', page + 1, rowsPerPage, JSON.stringify(filters)],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      console.log('Making API call with params:', params.toString());
      const response = await api.get(`/mint-requests?${params}`);
      return response.data;
    },
    keepPreviousData: true,
    staleTime: 5000, // Reduce stale time to 5 seconds for testing
    refetchOnWindowFocus: false
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['mintRequestStats'],
    queryFn: async () => {
      const response = await api.get('/mint-requests/stats/summary');
      return response.data;
    },
    staleTime: 60000 // 1 minute
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (newRequest) => {
      const response = await api.post('/mint-requests', newRequest);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries('mintRequests');
      queryClient.invalidateQueries('mintRequestStats');
      setOpenDialog(false);
      setEditingRequest(null);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }) => {
      const response = await api.put(`/mint-requests/${id}`, updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries('mintRequests');
      queryClient.invalidateQueries('mintRequestStats');
      setOpenDialog(false);
      setEditingRequest(null);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/mint-requests/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries('mintRequests');
      queryClient.invalidateQueries('mintRequestStats');
    }
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (request = null) => {
    setEditingRequest(request);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRequest(null);
  };

  const handleSubmit = (formData) => {
    if (editingRequest) {
      updateMutation.mutate({ id: editingRequest.id, updates: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this mint request?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleFilterChange = (field, value) => {
    console.log('Filter change:', field, value);
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      console.log('New filters:', newFilters);
      return newFilters;
    });
    setPage(0); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      userId: '',
      assetId: '',
      contract: '',
      tokenId: ''
    });
    setPage(0);
  };

  const handleViewRequest = async (request) => {
    setViewingRequest(request);
    setViewDialogOpen(true);
    setMetadata(null);
    
    // Fetch metadata if tokenId and contract are available
    if (request.tokenId && request.contract) {
      setMetadataLoading(true);
      try {
        const response = await fetch(
          `https://beta-api.paradisetycoon.com/api/metadata?tokenId=${request.tokenId}&contract=${request.contract}`
        );
        const data = await response.json();
        
        if (data.status.error_code === 0 && data.data && data.data.data) {
          // Parse the nested JSON string
          const parsedMetadata = JSON.parse(data.data.data);
          setMetadata(parsedMetadata);
        }
      } catch (error) {
        console.error('Error fetching metadata:', error);
      } finally {
        setMetadataLoading(false);
      }
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingRequest(null);
    setMetadata(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading mint requests...</Typography>
        </Stack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading mint requests: {error?.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  const mintRequests = mintRequestsData?.data || [];
  const pagination = mintRequestsData?.pagination || {};

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Mint Requests
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              console.log('Manual refresh clicked, current filters:', filters);
              queryClient.invalidateQueries('mintRequests');
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Mint Request
          </Button>
        </Stack>
      </Stack>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Requests
                </Typography>
                <Typography variant="h4">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {Object.entries(stats.byStatus).map(([status, count]) => (
            <Grid item xs={12} sm={6} md={3} key={status}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Typography>
                  <Typography variant="h4">
                    {count}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Accordion expanded={showFilters} onChange={() => setShowFilters(!showFilters)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterIcon />
            <Typography variant="h6">Filters</Typography>
            {Object.values(filters).some(v => v) && (
              <Chip label="Active" color="primary" size="small" />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.map(status => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="User ID"
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Asset ID"
                value={filters.assetId}
                onChange={(e) => handleFilterChange('assetId', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Token ID"
                value={filters.tokenId}
                onChange={(e) => handleFilterChange('tokenId', e.target.value)}
                placeholder="Search by token ID"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Contract"
                value={filters.contract}
                onChange={(e) => handleFilterChange('contract', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Table */}
      <Card sx={{ mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Token ID</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mintRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    <Tooltip title={request.id}>
                      <Typography variant="body2" sx={{ 
                        maxWidth: 100, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                      }}>
                        {request.id}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight="medium">
                        {request.userName || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {request.userEmail}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight="medium">
                        {request.assetTitle || 'Unknown Asset'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {request.assetType}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={request.status} 
                      color={STATUS_COLORS[request.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {request.tokenId || '-'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={request.contract}>
                      <Typography variant="body2" sx={{ 
                        maxWidth: 150, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                      }}>
                        {request.contract}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {new Date(request.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewRequest(request)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(request)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(request.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <MintRequestDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        request={editingRequest}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
        error={createMutation.error || updateMutation.error}
      />

      {/* View Dialog */}
      <MintRequestViewDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        request={viewingRequest}
        metadata={metadata}
        metadataLoading={metadataLoading}
      />
    </Box>
  );
}

// Dialog component for adding/editing mint requests
function MintRequestDialog({ open, onClose, onSubmit, request, isLoading, error }) {
  const [formData, setFormData] = useState({
    userId: '',
    assetId: '',
    contract: '',
    tokenId: '',
    referenceId: '',
    status: 'pending'
  });

  useEffect(() => {
    if (request) {
      setFormData({
        userId: request.userId || '',
        assetId: request.assetId || '',
        contract: request.contract || '',
        tokenId: request.tokenId || '',
        referenceId: request.referenceId || '',
        status: request.status || 'pending'
      });
    } else {
      setFormData({
        userId: '',
        assetId: '',
        contract: '',
        tokenId: '',
        referenceId: '',
        status: 'pending'
      });
    }
  }, [request, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Filter out empty values for updates
    const dataToSubmit = request 
      ? Object.fromEntries(Object.entries(formData).filter(([_, v]) => v !== ''))
      : formData;
    
    onSubmit(dataToSubmit);
  };

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {request ? 'Edit Mint Request' : 'Add New Mint Request'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error">
                {error?.response?.data?.error || error.message || 'An error occurred'}
              </Alert>
            )}
            
            <TextField
              label="User ID"
              value={formData.userId}
              onChange={(e) => handleChange('userId', e.target.value)}
              required={!request}
              disabled={!!request}
              fullWidth
              helperText={request ? "Cannot change user for existing request" : "UUID of the user"}
            />
            
            <TextField
              label="Asset ID" 
              value={formData.assetId}
              onChange={(e) => handleChange('assetId', e.target.value)}
              required={!request}
              disabled={!!request}
              fullWidth
              helperText={request ? "Cannot change asset for existing request" : "UUID of the asset to mint"}
            />
            
            <TextField
              label="Contract Address"
              value={formData.contract}
              onChange={(e) => handleChange('contract', e.target.value)}
              required={!request}
              fullWidth
              helperText="Smart contract address for minting"
            />
            
            <TextField
              label="Token ID"
              value={formData.tokenId}
              onChange={(e) => handleChange('tokenId', e.target.value)}
              fullWidth
              helperText="Token ID (optional, usually set after minting)"
            />
            
            <TextField
              label="Reference ID"
              value={formData.referenceId}
              onChange={(e) => handleChange('referenceId', e.target.value)}
              required={!request}
              fullWidth
              helperText="External reference ID for tracking"
              InputProps={{
                endAdornment: !request && (
                  <Button
                    size="small"
                    onClick={() => handleChange('referenceId', generateUUID())}
                  >
                    Generate
                  </Button>
                )
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(status => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {request ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              request ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// View Dialog component for viewing mint request details and metadata
function MintRequestViewDialog({ open, onClose, request, metadata, metadataLoading }) {
  if (!request) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Mint Request Details</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Request Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Request Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Request ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {request.id}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip 
                        label={request.status} 
                        color={STATUS_COLORS[request.status] || 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      User
                    </Typography>
                    <Typography variant="body2">
                      {request.userName || 'Unknown User'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {request.userEmail}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Asset
                    </Typography>
                    <Typography variant="body2">
                      {request.assetTitle || 'Unknown Asset'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Type: {request.assetType}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Contract Address
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {request.contract}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Token ID
                    </Typography>
                    <Typography variant="body2">
                      {request.tokenId || 'Not minted yet'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Reference ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {request.referenceId}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {new Date(request.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Updated
                    </Typography>
                    <Typography variant="body2">
                      {new Date(request.updatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Metadata Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  NFT Metadata
                </Typography>
                
                {!request.tokenId ? (
                  <Alert severity="info">
                    Token ID not available. Metadata will be shown after minting.
                  </Alert>
                ) : metadataLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <Stack alignItems="center" spacing={2}>
                      <CircularProgress />
                      <Typography>Loading metadata...</Typography>
                    </Stack>
                  </Box>
                ) : metadata ? (
                  <Stack spacing={2}>
                    {/* NFT Image */}
                    {metadata.image && (
                      <Box>
                        <img
                          src={metadata.image}
                          alt={metadata.name}
                          style={{
                            width: '100%',
                            maxHeight: '300px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                        />
                      </Box>
                    )}
                    
                    {/* NFT Video */}
                    {metadata.animation_url && (
                      <Box>
                        <video
                          controls
                          style={{
                            width: '100%',
                            maxHeight: '300px',
                            borderRadius: '8px'
                          }}
                        >
                          <source src={metadata.animation_url} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </Box>
                    )}
                    
                    {/* NFT Name */}
                    <Box>
                      <Typography variant="h6">
                        {metadata.name}
                      </Typography>
                    </Box>
                    
                    {/* NFT Description */}
                    {metadata.description && (
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Description
                        </Typography>
                        <Typography variant="body2">
                          {metadata.description}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Attributes */}
                    {metadata.attributes && metadata.attributes.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Attributes
                        </Typography>
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          {metadata.attributes.map((attr, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Card variant="outlined" sx={{ p: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                  {attr.trait_type}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {attr.value}
                                </Typography>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                    
                    {/* External Link */}
                    {metadata.external_url && (
                      <Box>
                        <Button
                          variant="outlined"
                          href={metadata.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          fullWidth
                        >
                          View on Paradise Tycoon
                        </Button>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="warning">
                    Failed to load metadata. The token may not exist yet or there may be a network issue.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
} 